/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from 'react';
import { css, useTheme } from '@emotion/react';
import highriskicon from "../../../assets/highrisk.svg";
import mediumriskicon from "../../../assets/mediumrisk.svg";
import rewriteIcon from '../../../assets/rewrite.svg';
import flaggedicon from "../../../assets/flagged_awareness.svg";
import Rewrite from './Rewrite';
import { generateBulkScrutinizedWord, generateScrutinizedWord, getAllScrutinizedWord, getAllScrutinizedWordList } from 'services/scrutinizedWordService';
import unproductiveicon from "../../../assets/Unproductive-risk.svg";
import { Icon, useToast } from '@coreai/component-library';
import { useLanguage } from 'LanguageContext';
import { useAnalysis } from '../Analyses/FormComponents/AnalysisResultContext';
import VBars from 'components/CommonUi/Loaders/VBars';
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 1.5rem;
    background-color: #F2F6F8;
  `,
  header: css`
    font-size: 1rem;
    font-weight: 600;
    color: #000;
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 6px;
    align-items: center;
  `,
  subtext: css`
    font-size: 10px;
    font-weight: 500;
    color: #000;
    line-height: 150%;
    letter-spacing: 0.2px;
    margin-top: 4px;
  `,
  riskBlock: (risk: 'High' | 'Medium' | 'Unproductive' | 'Low') => css`
    border: 1px solid #ACB8BD;
    background-color: #fff;
    border-radius: 12px;
    margin-bottom: 1rem;
    > div:not(:first-child) {
      border-top: 1px solid #ACB8BD;
    }
  `,
  tagWrapper:(risk: 'High' | 'Medium' | 'Unproductive' | 'Low') => {
    const colors = {
      High: { background: '#FFEEEE' },
      Medium: { background: '#FFF9CF' },
      Unproductive: {background: '#F1E2FF'},
      Low: { background: '#DEECF8' },
    };
    return css`
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 2px 4px;
      border-radius: 4px;
      background-color: ${colors[risk].background};
      color: #000;
      font-weight: 600;
      font-size: 12px;
    `;
  },
  contentWrapper: css`
    padding: 12px 24px;
  `,
  pageTag: css`
    padding: 2px 12px;
    font-size: 0.75rem;
    font-weight: 600;
    border-radius: 4px;
    display: inline-block;
    margin-bottom: 6px;
    line-height: 150%;
    color: #607A8C;
    border: 1px solid #ACB8BD;
  `,
  quote: css`
    margin-bottom: 0.5rem;
    font-size: 14px;
    font-weight: 500;
  `,
  highlightedTags: (risk: 'High' | 'Medium' | 'Unproductive' | 'Low') => {
    const colors = {
      High: { color: '#B82223', background: '#FFEEEE' },
      Medium: { color: '#AD5F00', background: '#FFF9CF' },
      Unproductive: {color: '#8441C6', background: '#F1E2FF'},
      Low: { color: '#5894D9', background: '#DEECF8' },
    };
    return css`
      color: ${colors[risk].color};
      background-color: ${colors[risk].background};
      font-weight: 600;
      padding: 2px;
    `;
  },
  separator: css`
    border-bottom: 1px dashed #ACB8BD;
    margin: 8px 0;
  `,
  rewriteRow: (showRewrite: boolean) => css`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    width: 100%;
    transition: all 0.3s ease;
    > div:first-of-type {
      flex: ${showRewrite ? "1 1 50%" : "1 1 75%"};
      > p {
       margin-top: 0px;
      }
    }
  `,
  rewriteText: css`
    margin: 0;
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 150%;
    color: #000;
  `,
  rewriteIconWrapper: css`
    width: 22px;
    height: 22px;
    display: block;
  `,
  rewriteButton: (bulkLoading: boolean) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    background: transparent;
    transition: all 0.3s ease;
    cursor: ${bulkLoading ? "default" : "pointer"};
    opacity: ${bulkLoading ? "0.6" : "1"};
    
    >img{
      width: 22px;
      height: 22px;
    }
  `,
};

export interface Occurrence {
  word?: string;
  exact_wording?: string;
  page_context: string;
  occurence_index: number;
  sentence_context: string;
  is_rewritten: boolean;
}

export interface RiskWordItem {
  link: string;
  word: string;
  index: number;
  rationale: string;
  occurrences: Occurrence[];
  polarization_level: "High" | "Medium" | "Unproductive" | "Low";
  number_of_occurences: number;
  occurrence_rewritten_count?: number;
  rewrite_tip: string | null;
  alternatives: string | null;
}

interface ESGFlaggingProps {
  rowDataList: RiskWordItem;
}

const ESGFlaggingPanel = ({ rowDataList }: ESGFlaggingProps) => {
  const [expandedIndexes, setExpandedIndexes] = useState(true);
  const [rewriteOpenIndexes, setRewriteOpenIndexes] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [generatedWord, setGeneratedWord] = useState<Record<string, any>>({});
  const [bulkLoading, setBulkLoading] = useState(true);
  const [bulkGeneratedWords, setBulkGeneratedWords] = useState<any[]>([]);

  const {analysisContextResult} = useAnalysis();
  const { t } = useLanguage();
  const theme = useTheme();
  const { toast } = useToast();

  const toggleExpand = () => setExpandedIndexes((prev) => !prev);

  const toggleRewriteVisibility = (key: string | number) => {
    setRewriteOpenIndexes(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };  

  const fetchRewriteData = async (
    key: string | number,
    risk_assesmnt_id: number,
    word_index: number,
    occurence_index: number,
    payload: any,
    mode = "generate"     // "generate" | "getAll" | "regenerate"
  ) => {
    setLoading(prev => ({ ...prev, [key]: true }));
  
    try {
      let response: any;
  
      if (mode === "getAll") {
        response = await getAllScrutinizedWord(
          risk_assesmnt_id,
          word_index,
          occurence_index
        );
        if (response?.results) {
          setGeneratedWord(prev => ({
            ...prev,
            [key]: response.results,
          }));
        }
      } else if (mode === "generate" || mode === "regenerate") {
        response = await generateScrutinizedWord(
          risk_assesmnt_id,
          word_index,
          occurence_index,
          payload
        );
  
        if (response?.success) {
          setGeneratedWord(prev => {
            const existing = prev[key] ?? [];
  
            // prevent duplicates
            const isDuplicate = existing.some(
              (item: any) => item.id === response.data.id
            );
  
            return {
              ...prev,
              [key]: isDuplicate ? existing : [...existing, response.data],
            };
          });
        }
      }
      return response;
    } catch (err: any) {
      toast({
        variant: "error",
        title: err?.response?.data?.message || "Error occurred.",
      });
      return null;
    } finally {
      setLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const fetchRewriteAllData = async (
    risk_assesmnt_id: number,
    word_index: number,
    mode: "generateAll" | "getAllList",
    showAll: boolean = false,
  ) => {
    setBulkLoading(true);
  
    try {
      let response: any;
      const occurrenceKeyMap: Record<string, string> = {};

      //Get previously generated rewrites
      if (mode === "getAllList") {
        response = await getAllScrutinizedWordList(
          risk_assesmnt_id,
          word_index
        );
      
        if (response?.data) {
      
          Object.entries(groupedEntries).forEach(([page, entries]) => {
            entries.forEach((occ, i) => {
              const occIndex = String(occ.occurence_index ?? i);
              occurrenceKeyMap[occIndex] = `${page}-${i}`;
            });
          });
          setGeneratedWord(prev => {
            const next = { ...prev };
            const bulk = response.data as Record<string, any[]>;
            Object.entries(bulk).forEach(([occIndex, list]) => {
              const uiKey = occurrenceKeyMap[occIndex];
              if (!uiKey) return;
      
              next[uiKey] = list;
            });
      
            return next;
          });
        }
      } else if (mode === "generateAll") {
        response = await generateBulkScrutinizedWord(
          risk_assesmnt_id,
          word_index
        );
  
        if (response?.success && response.data) {
          setBulkGeneratedWords(response.data);
  
          Object.entries(groupedEntries).forEach(([page, entries]) => {
            entries.forEach((entry: any, i: number) => {
              const uiKey = `${page}-${i}`;
              occurrenceKeyMap[String(entry.occurence_index)] = uiKey;
            });
          });
  
          setGeneratedWord(prev => {
            const updated = { ...prev };
  
            Object.entries(response.data).forEach(([occIndex, list]) => {
              const items = list as any[];
            
              const uiKey = occurrenceKeyMap[occIndex];
              if (!uiKey) return;
            
              const existing = updated[uiKey] ?? [];
            
              items.forEach(item => {
                if (!existing.some((e: any) => e.id === item.id)) {
                  updated[uiKey] = [...(updated[uiKey] ?? []), item];
                }
              });
            });
            
  
            return updated;
          });
  
        }
      }
      if(showAll) {
        //auto-open panels that now have rewrites
        setRewriteOpenIndexes(prev => {
          const next = { ...prev };

          Object.keys(response.data).forEach(occIndex => {
            const uiKey = occurrenceKeyMap[occIndex];
            if (uiKey) next[uiKey] = true;
          });

          return next;
        });
      }
  
      return response;
    } catch (err: any) {
      toast({
        variant: "error",
        title: err?.response?.data?.message || "Error occurred.",
      });
      return null;
    } finally {
      setBulkLoading(false);
    }
  };

  const rewrittenIcon = () => css`
    color: ${theme.colors.primary};
    width: 22px;
    height: 22px;
    background-color: #F2F6F8;
    cursor: pointer;
    border-radius: 50%;
    padding: 4px;
    transform: translateY(-1.5px);
  `;
  
  const rewrittenText = () => css`
    margin: 0;
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 150%;
    color: ${theme.colors.primary};
  `;

  const subtextTitle = () => css`
    font-size: 10px;
    font-weight: 600;
    color: #000;
    line-height: 150%;
    margin-top: 0px;
    letter-spacing: 0.2px;
    white-space: normal;
  `;

  useEffect(() => {
    if (analysisContextResult?.risk_assessment?.[0]?.id && rowDataList?.index !== undefined) {
      fetchRewriteAllData(analysisContextResult?.risk_assessment?.[0]?.id, rowDataList?.index, "getAllList");
    }
  }, [])

  const handleRewriteClick = (
    key: any,
    mode: string,   // "open" | "close" | "regenerate" | "getAll"
    risk_assesmnt_id: any,
    word_index: any,
    occurence_index: any,
    payload: any
  ) => {

    if (!["close","open","regenerate","getAll"].includes(mode)) return Promise.reject();
  
    if (["close","open","getAll"].includes(mode)) toggleRewriteVisibility(key);

    if (mode === "close") return Promise.resolve();

    return fetchRewriteData(key, risk_assesmnt_id, word_index, occurence_index, payload, mode === "open" ? "generate" : mode);

  };  
  
  const handleRewriteAllClick = (
    risk_assesmnt_id: any,
    word_index: any,
    mode: "generateAll" | "getAllList",
    showAll: boolean = false,
  ) => {
    return fetchRewriteAllData(risk_assesmnt_id, word_index, mode, showAll);
  };

  const getRiskLabel = (risk: 'High' | 'Medium' | 'Unproductive' | 'Low') => {
    const icons = { High: highriskicon, Medium: mediumriskicon, Unproductive: unproductiveicon, Low: flaggedicon };
    const labels = {
      High: t("risk_flagging_words_high", null, analysisContextResult?.locale_code),
      Medium: t("risk_flagging_words_medium", null, analysisContextResult?.locale_code),
      Unproductive: t("risk_flagging_words_unproductive", null, analysisContextResult?.locale_code),
      Low: t("risk_flagging_words_awareness", null, analysisContextResult?.locale_code),
    };
    return (
      <div css={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <div css={styles.tagWrapper(risk)}>
          <img src={icons[risk]} alt={risk} width={12} /> {labels[risk]}
        </div>
        {rowDataList?.occurrences?.length}
      </div>
    );
  };

  const Highlight = ({ text, highlight }: { text: string; highlight: string }) => {
    if (!highlight) return <Markdown rehypePlugins={[rehypeRaw]}>{text}</Markdown>;
    const regex = new RegExp(`(${highlight})`, "gi");
    return (
      <>
        {text.split(regex).map((part, i) =>
          regex.test(part) ? (
            <mark key={i} css={styles.highlightedTags(rowDataList.polarization_level)}>
              {part}
            </mark>
          ) : (
            part
          )
        )}
      </>
    );
  };
  
  const groupedEntries = {} as Record<string, Occurrence[]>;
  
  rowDataList?.occurrences.forEach((occurrence, index) => {
    if (!occurrence?.occurence_index) occurrence.occurence_index = index;
    if (!groupedEntries[occurrence.page_context]) groupedEntries[occurrence.page_context] = [];
    groupedEntries[occurrence.page_context].push(occurrence);
  });

  const allRewritten = [rowDataList?.occurrences?.filter(o => o.is_rewritten).length, Object.keys(generatedWord).length].includes(rowDataList?.occurrences?.length);

  return (
    <div css={styles.container}>
      <div css={styles.header}>
        <div>
          <p css={subtextTitle}>{t("risk_flagging_words_title", null, analysisContextResult?.locale_code)} "{rowDataList?.word}"</p>
          <div css={styles.subtext}>{rowDataList?.rationale}</div>
          {(rowDataList?.rewrite_tip != null && rowDataList?.alternatives == null) &&
            <div style={{marginTop: "8px"}}>
              <p css={subtextTitle}>{t("risk_flagging_rewrite_tip_title", null, analysisContextResult?.locale_code)}</p>
              <div css={styles.subtext}>{rowDataList?.rewrite_tip}</div>
            </div>
          }
        </div>
        {
          rowDataList?.alternatives != null ?
          (bulkLoading ? (
            <>
              <div style={{ padding: "20px" }}>
                <VBars size="small" />
              </div>
            </>
          ) : (

          allRewritten ? (
            <div
            css={styles.rewriteButton(bulkLoading)}
            onClick={() => {
              return handleRewriteAllClick(
                analysisContextResult?.risk_assessment?.[0]?.id,
                rowDataList?.index,
                "getAllList",
                true
              )
            }}
            >
              <Icon name="eye" size="xs" css={rewrittenIcon}/>
              <p css={rewrittenText}>{t("rephrased_rewritten", null, analysisContextResult?.locale_code)}</p>
            </div>
          ) : (
            <div 
              css={styles.rewriteButton(bulkLoading)} 
              onClick={() => {
                return handleRewriteAllClick(
                  analysisContextResult?.risk_assessment?.[0]?.id,
                  rowDataList?.index,
                  "generateAll",
                  true
                )
              }
              }
            >
              <div css={styles.rewriteIconWrapper}><img src={rewriteIcon} alt="rewrite all" /></div>
              <p css={styles.rewriteText}>{t("rephrased_rewrite", null, analysisContextResult?.locale_code)}</p>
            </div>
          )
        )) : null
        }
      </div>

      <div css={styles.riskBlock(rowDataList.polarization_level)}>

        {expandedIndexes &&
          Object.entries(groupedEntries || {}).map(([page, entries]) => (
            <div key={page} css={styles.contentWrapper}>
              <div css={styles.pageTag}>Page {page}</div>

              {entries.map((entry, i) => {
                const uiKey = `${page}-${i}`;
                const showRewrite = rewriteOpenIndexes[uiKey] || false;
              
                const childPayload = {
                  word: rowDataList?.word,
                  rationale: rowDataList?.rationale,
                  exact_wording: entry?.exact_wording || entry?.word,
                  sentence_context: entry?.sentence_context,
                };
                return (
                  <div key={uiKey} data-occurrence-index={entry?.occurence_index || i} className='riskflagging-word-wrapper' css={styles.quote}>
                    <div css={styles.rewriteRow(showRewrite)}>
                      <div>
                        <Highlight text={entry?.sentence_context} highlight={ entry?.sentence_context.includes("<mark")? "" : (entry?.exact_wording || entry?.word || "") } />
                      </div>
                      {
                        rowDataList?.alternatives != null ?
                        ((loading[uiKey] && !generatedWord[uiKey]) || bulkLoading ? 
                        <div style={{ padding: "40px 80px" }}>
                          <VBars size="small" />
                        </div>
                        :
                        <Rewrite
                          showRewrite={showRewrite}
                          rewriteKey={uiKey}
                          handleRewriteClick={(mode, payload) => {

                            return handleRewriteClick(
                              uiKey,
                              mode,
                              analysisContextResult?.risk_assessment?.[0]?.id,
                              rowDataList?.index,
                              entry?.occurence_index ?? i,
                              payload ?? childPayload
                            )
                          }
                          }
                          occurence={{...entry, is_rewritten: (generatedWord?.[uiKey]?.length ?? 0) > 0}}
                          restGeneratedWordOccurences={() => {
                            groupedEntries[page][i].is_rewritten = false;
                            delete generatedWord[uiKey];
                          }}
                          generatedWord={generatedWord[uiKey]}
                          loading={loading[uiKey]}
                        />)
                        : null
                      }
                    </div>
                    {i < entries.length - 1 && <div css={styles.separator}></div>}
                  </div>
                );
              })}
            </div>
          ))}
      </div>
    </div>
  );
};

export default ESGFlaggingPanel;